import { isDefined, isNotDefined } from '@sgme/fp';

import { optionApi } from '@/api/option';
import { createRfsRequestParams } from '@/api/option/requestOptionRfs.ts';
import { tradeCaptureApi } from '@/api/trade-capture';
import type { CheckOptionParams, NullableLegProperties } from '@/api/trade-capture/option/checkOption.ts';
import { getRfsId } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type {
  LegId,
  LegPropertiesSliceWithId,
  OptionLegProperties,
  OptionProductName,
  OptionUiState,
} from '@/store/option/state.ts';
import { checkOptionThunk } from '@/store/option/thunks/checkOptionThunk.ts';
import { getNewLegId, shallowMergeLegs } from '@/store/option/utils.ts';
import type { AppState } from '@/store/store.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';
import { selectTileClientId, selectTilesOfTab } from '@/store/tiles/selectors.ts';
import type { CurrencyChoice } from '@/types/common.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

export const cancelAllOptionsRfsThunk = createAppAsyncThunk<void, void>('global/cancelRfs', async (_, api) => {
  const state: AppState = api.getState();
  const currentTabId = selectActiveTabId(state);
  const tiles = selectTilesOfTab(state, currentTabId);

  const cancellationActions = tiles
    .map(({ tileId }) => {
      const option = optionSlice.selectors.getOptionTile(state, tileId);
      const rfsId = isDefined(option) ? getRfsId(option) : undefined;
      if (isDefined(option) && isDefined(rfsId)) {
        return optionApi.endpoints.cancelOptionRfs.initiate({
          tileId: tileId,
          rfsId: rfsId,
          removeQuote: true,
        });
      }
      return undefined;
    })
    .filter(isDefined);
  await Promise.all(cancellationActions.map((cancelAction) => api.dispatch(cancelAction)));
});

interface OptionsRfsThunkParams {
  tileId: string;
  forceTradeable: boolean;
  skipCreditCheck?: boolean;
}

export const requestOptionsRfsThunk = createAppAsyncThunk<void, OptionsRfsThunkParams>(
  'option/requestRfs',
  async (params, { dispatch, getState }) => {
    const { tileId, forceTradeable, skipCreditCheck = false } = params;

    let option = getState().option[tileId];
    if (isNotDefined(option)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    let rfsId = getRfsId(option);

    if (isDefined(rfsId)) {
      await dispatch(
        optionApi.endpoints.cancelOptionRfs.initiate({
          tileId,
          rfsId,
        }),
      );
    }

    option = getState().option[tileId];
    if (isNotDefined(option)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    rfsId = getRfsId(option);
    const newRfsId = isDefined(rfsId) ? rfsId : getNewRfsId();
    const clientId = selectTileClientId(getState(), tileId);
    const rfsRequestParams = createRfsRequestParams({
      option: option,
      tileId: tileId,
      rfsId: newRfsId,
      clientId: clientId,
      forceTradeable: forceTradeable,
      skipCreditCheck,
    });
    await dispatch(optionApi.endpoints.requestOptionRfs.initiate(rfsRequestParams, { fixedCacheKey: newRfsId }));
  },
);

export type ResetOptionThunkParams = {
  tileId: string;
  currencyPair: string | undefined;
  newType: OptionProductName;
  uiState?: OptionUiState;
  clientId?: number;
};

export const resetOptionThunk = createAppAsyncThunk<void, ResetOptionThunkParams>(
  'option/reset',
  async (params, { dispatch, getState }) => {
    const { tileId, currencyPair, newType, uiState, clientId } = params;
    let option = optionSlice.selectors.getOptionTile(getState(), tileId);
    if (isNotDefined(option)) {
      return;
    }

    const rfsId = getRfsId(option);
    const userPreferences = getState().userPreferences;

    dispatch(optionSlice.actions.reset({ tileId, newType, userPreferences, currencyPair, uiState, clientId }));
    if (isDefined(rfsId)) {
      dispatch(optionApi.endpoints.cancelOptionRfs.initiate({ tileId, rfsId }));
    }

    option = optionSlice.selectors.getOptionTile(getState(), tileId);
    // cannot TC reset without currencyPair
    if (isNotDefined(option) || isNotDefined(currencyPair)) {
      return;
    }

    await dispatch(
      checkOptionThunk({
        type: 'init',
        tileId,
        shouldApplyUserPrefs: true,
      }),
    );
  },
);

export const removeOptionLegThunk = createAppAsyncThunk<void, { tileId: string; legId: LegId }>(
  'option/removeLeg',
  async (params, { dispatch, getState }) => {
    const { tileId, legId } = params;
    const optionTile = optionSlice.selectors.getOptionTile(getState(), tileId);
    const optionProperties = optionTile?.properties;
    const tradeCapture = optionTile?.tradeCapture;
    const product = optionTile?.type;

    if (isNotDefined(tradeCapture) || isNotDefined(product)) {
      return;
    }
    const legProperties = optionProperties?.legs
      ?.filter((leg) => leg.legId !== legId)
      .map((leg) => ({ legId: leg.legId }));

    const query: CheckOptionParams = {
      tileId,
      tradeCapture,
      productName: product,
      legProperties,
      optionState: optionTile,
    };

    // we voluntarily omit the leg from the TC call
    await dispatch(tradeCaptureApi.endpoints.checkOption.initiate(query));
  },
);

export const changeOptionTypeThunk = createAppAsyncThunk<
  void,
  { tileId: string; legProductName: 'Vanilla' | 'Trigger' | 'Binary'; legId: `${string}/${number}` }
>('option/changeType', async (params, { dispatch, getState }) => {
  const { tileId, legProductName, legId } = params;
  const optionTile = optionSlice.selectors.getOptionTile(getState(), tileId);
  if (isNotDefined(optionTile)) {
    return;
  }
  const isMultiLeg = optionTile.properties.legs.length > 1;
  dispatch(optionSlice.actions.changeLegProductName({ tileId, legId, legProductName }));

  dispatch(
    checkOptionThunk({
      tileId,
      productName: isMultiLeg ? 'Vanilla' : legProductName,
      properties: {
        currencyPair: optionTile.properties.currencyPair,
      },
      legProperties: {
        legId,
        barrier1: legProductName !== 'Vanilla' ? {} : undefined,
      },
    }),
  );
});

export const addOptionLegThunk = createAppAsyncThunk<void, { tileId: string }>(
  'option/addLeg',
  async (params, { dispatch, getState }) => {
    const { tileId } = params;
    const optionTile = optionSlice.selectors.getOptionTile(getState(), tileId);
    if (isNotDefined(optionTile)) {
      return;
    }

    const linkedState = optionTile.properties.uiState.legsNotionalCurrenciesLinkState;
    const legs = optionTile.properties.legs;
    dispatch(
      checkOptionThunk({
        tileId,
        properties: {
          // this is required for the fake to initialize the leg :'(
          currencyPair: optionTile.properties.currencyPair,
          premiumType: optionTile.properties.premiumType.premiumCategory === "VOLATILITY" ? { premiumCurrency: optionTile?.properties.premiumType.premiumCurrency, premiumCategory: "AMOUNT" } : optionTile.properties.premiumType,
        },
        legProperties: {
          legId: getNewLegId(tileId, legs),
          legProductName: 'Vanilla',
          notionalCurrencyIndex: linkedState === 'linked' ? legs[0]?.notionalCurrencyIndex : undefined,
        },
      }),
    );
  },
);

export const duplicateOptionLegThunk = createAppAsyncThunk<void, { tileId: string; legId: LegId }>(
  'option/duplicateLeg',
  async (params, { dispatch, getState }) => {
    const { tileId, legId } = params;
    const optionTile = optionSlice.selectors.getOptionTile(getState(), tileId);
    const optionProperties = optionTile?.properties;
    const tradeCapture = optionTile?.tradeCapture;
    const product = optionTile?.type;

    if (isNotDefined(tradeCapture) || isNotDefined(product)) {
      return;
    }
    const legs = optionProperties?.legs ?? [];
    const duplicate = legs.find((leg) => leg.legId === legId);
    if (duplicate === undefined) {
      return;
    }

    const newLegId = getNewLegId(tileId, legs);
    const legProperties = shallowMergeLegs(legs, [
      {
        ...duplicate,
        legId: newLegId,
        // do not duplicate solving strike, only one strike is solvable at the same time
        strike: duplicate.strike?.type === 'solving' ? undefined : duplicate.strike,
      },
    ]);
    const query: CheckOptionParams = {
      tileId,
      tradeCapture,
      productName: duplicate.legProductName,
      legProperties,
      optionState: optionTile,
      properties: {
        currencyPair: optionTile?.properties.currencyPair,
        premiumType: optionTile?.properties.premiumType.premiumCategory === "VOLATILITY" ? { premiumCurrency: optionTile?.properties.premiumType.premiumCurrency, premiumCategory: "AMOUNT" } : optionTile?.properties.premiumType,
      },
    };

    await dispatch(tradeCaptureApi.endpoints.checkOption.initiate(query));
  },
);

type ChangeLinkedCurrencyPayload =
  | { type: 'toggle'; tileId: string }
  | { type: 'changeValue'; legId: LegId; tileId: string; currency: CurrencyChoice };

export const changeLinkedCurrency = createAppAsyncThunk<void, ChangeLinkedCurrencyPayload>(
  'option/changeLinkedCurrency',
  async (params, { dispatch, getState }) => {
    const { tileId, type } = params;
    const optionTile = optionSlice.selectors.getOptionTile(getState(), tileId);
    if (isNotDefined(optionTile)) {
      return;
    }

    const currentLinkedState = optionTile.properties.uiState.legsNotionalCurrenciesLinkState;
    const legs = optionTile.properties.legs;

    function updateLegsCurrencies(legs: OptionLegProperties[], currency: CurrencyChoice) {
      const tcUpdates: LegPropertiesSliceWithId[] = [];
      const stateUpdates: { legId: LegId; currency: CurrencyChoice }[] = [];
      for (const leg of legs) {
        if (isDefined(leg.notional)) {
          tcUpdates.push({
            legId: leg.legId,
            notional: leg.notional,
            notionalCurrencyIndex: currency,
          });
        } else {
          stateUpdates.push({
            legId: leg.legId,
            currency: currency,
          });
        }
      }

      if (stateUpdates.length > 0) {
        dispatch(optionSlice.actions.setLegCurrencies({ tileId, legCurrencies: stateUpdates }));
      }

      if (tcUpdates.length > 0) {
        dispatch(checkOptionThunk({ tileId, legProperties: tcUpdates }));
      }
    }

    switch (type) {
      case 'toggle': {
        const newState = currentLinkedState === 'linked' ? 'unlinked' : 'linked';
        dispatch(optionSlice.actions.setLegsNotionalLinkedCurrency({ tileId, linked: newState }));
        if (newState === 'linked') {
          updateLegsCurrencies(legs, legs[0]?.notionalCurrencyIndex ?? 1);
        }

        return;
      }
      case 'changeValue': {
        if (currentLinkedState === 'linked') {
          updateLegsCurrencies(legs, params.currency);
        } else {
          updateLegsCurrencies(
            legs.filter((leg) => leg.legId === params.legId),
            params.currency,
          );
        }

        return;
      }
    }
  },
);

export const replaceOptionLegsThunk = createAppAsyncThunk<void, { tileId: string; legs: NullableLegProperties[] }>(
  'option/replaceLegs',
  async (params, { dispatch, getState }) => {
    const { tileId, legs } = params;
    const optionTile = optionSlice.selectors.getOptionTile(getState(), tileId);
    if (isNotDefined(optionTile)) {
      return;
    }

    const query: CheckOptionParams = {
      tileId,
      legProperties: legs,
      hedges: [],
      tradeCapture: optionTile.tradeCapture,
      productName: optionTile.type,
      properties: {
        currencyPair: optionTile.properties.currencyPair,
      },
    };

    await dispatch(tradeCaptureApi.endpoints.checkOption.initiate(query));
  },
);
